// Pure presence-model helpers for the Dashboard (Brief 4 / P8, was P1). No
// React, no I/O — just the ordering, grouping, and labelling the sessions
// render, so the "which one needs me" and "which project is this in" logic is
// unit-tested without a DOM. The ONLY saturated / moving element is the
// needs_input attention signal (design: `signal.attention` is the one reserved
// moving token); every other session stays calm and still.

import type { NeedsInputReason, SessionPresence } from "@waytide/core";

export const PRESENCE_NEEDS_INPUT = "needs_input";

/** The id of the "Unassigned" bucket — a session with no project (its
 * `project_id` is 0 or absent) groups here. */
export const UNASSIGNED_PROJECT_ID = 0;

/** Whether a session is waiting on the human — the one state the Dashboard
 * self-orders to the top and paints with the attention signal. */
export function isNeedsInput(status: string): boolean {
  return status === PRESENCE_NEEDS_INPUT;
}

/** The needs-input reason rendered in plain WORDS (never the raw enum, never
 * free text). "" / absent / unknown → "" (no reason line). */
export function reasonLabel(reason: NeedsInputReason | undefined): string {
  switch (reason) {
    case "decision":
      return "Needs a decision";
    case "permission":
      return "Waiting on permission";
    case "blocked":
      return "Blocked — needs you";
    case "clarification":
      return "Needs clarification";
    default:
      return "";
  }
}

/** A calm, human label for a presence status. Open vocabulary — an unknown
 * status falls back to the raw string, treated calmly, never thrown. */
export function statusLabel(status: string): string {
  switch (status) {
    case "running":
      return "Running";
    case "idle":
      return "Idle";
    case "paused":
      return "Paused";
    case "halted":
      return "Halted";
    case "done":
      return "Done";
    case PRESENCE_NEEDS_INPUT:
      return "Needs you";
    default:
      return status;
  }
}

/** Which session-cell token family a session paints with. `needs-me` is the
 * ONLY tone that also animates (the attention signal); the rest are calm. */
export type CellTone = "needs-me" | "running" | "calm";

export function cellTone(session: SessionPresence): CellTone {
  if (isNeedsInput(session.status)) return "needs-me";
  if (session.status === "running") return "running";
  return "calm";
}

/** Order sessions for the "who needs me" glance: every `needs_input` session
 * first, the one that has WAITED LONGEST (oldest `updated_at`) at the very top
 * — so the most-overdue session is what the eye lands on. Everyone else follows
 * by most-recent activity. Pure + stable; never mutates the input. */
export function orderSessions(sessions: SessionPresence[]): SessionPresence[] {
  const needs = sessions.filter((s) => isNeedsInput(s.status));
  const rest = sessions.filter((s) => !isNeedsInput(s.status));
  needs.sort((a, b) => a.updated_at - b.updated_at); // longest-waiting first
  rest.sort((a, b) => b.updated_at - a.updated_at); // most-recent first
  return [...needs, ...rest];
}

/** The project a session belongs to for Dashboard grouping: its `project_id`,
 * or UNASSIGNED_PROJECT_ID (0) when unset / absent. */
export function sessionProjectId(session: SessionPresence): number {
  return session.project_id && session.project_id > 0
    ? session.project_id
    : UNASSIGNED_PROJECT_ID;
}

/** The sessions assigned to `projectId`, already ordered for the glance. Pass
 * UNASSIGNED_PROJECT_ID (0) for the Unassigned bucket. Pure. */
export function sessionsInProject(
  sessions: SessionPresence[],
  projectId: number,
): SessionPresence[] {
  return orderSessions(
    sessions.filter((s) => sessionProjectId(s) === projectId),
  );
}

/** Sessions per project id (the 0/Unassigned bucket included) — the sidebar's
 * count badges. */
export function projectSessionCounts(
  sessions: SessionPresence[],
): Map<number, number> {
  const counts = new Map<number, number>();
  for (const s of sessions) {
    const id = sessionProjectId(s);
    counts.set(id, (counts.get(id) ?? 0) + 1);
  }
  return counts;
}

/** Sessions that NEED the human, per project id — drives the sidebar's
 * per-project needs-me badge (the one place Notice reaches in the nav). */
export function projectNeedsCounts(
  sessions: SessionPresence[],
): Map<number, number> {
  const counts = new Map<number, number>();
  for (const s of sessions) {
    if (!isNeedsInput(s.status)) continue;
    const id = sessionProjectId(s);
    counts.set(id, (counts.get(id) ?? 0) + 1);
  }
  return counts;
}

/** A quiet relative-time for a session's last observed activity. `microsAt` is
 * micros since the unix epoch (the wire convention); `nowMs` is ms (injected so
 * it is pure + testable). Calm phrasing, never a churning live counter. */
export function relativeActivity(microsAt: number, nowMs: number): string {
  const diffMs = nowMs - microsAt / 1000;
  if (diffMs < 60_000) return "just now";
  if (diffMs < 3_600_000) return `${Math.floor(diffMs / 60_000)}m ago`;
  if (diffMs < 86_400_000) return `${Math.floor(diffMs / 3_600_000)}h ago`;
  return `${Math.floor(diffMs / 86_400_000)}d ago`;
}

/** Apply one live `presence` SSE frame to the current session map: upsert by
 * `thread_id`, or REMOVE the session when it reaches `done` (a finished session
 * leaves the Dashboard). Pure — returns a NEW map, never mutates the input.
 *
 * The current backend always merges the sidecar fields (`project_id` from
 * `session_project`, `machine_id` from `session_machine`) onto every presence
 * frame, so a present value — INCLUDING 0 (Unassigned) or "" (unattributed) —
 * is authoritative and is taken as-is (an explicit un-assign / un-attribute is
 * honoured live). We only PRESERVE the last known value when the frame OMITS the
 * field entirely (`undefined`) — an older backend that didn't merge it yet — so
 * a rollout-era frame can't drop a session out of its project. */
export function applyPresenceFrame(
  prev: Map<number, SessionPresence>,
  frame: SessionPresence,
): Map<number, SessionPresence> {
  const next = new Map(prev);
  if (frame.status === "done") {
    next.delete(frame.thread_id);
    return next;
  }
  const existing = prev.get(frame.thread_id);
  const merged: SessionPresence = { ...frame };
  if (merged.project_id === undefined && existing?.project_id) {
    merged.project_id = existing.project_id;
  }
  if (merged.machine_id === undefined && existing?.machine_id) {
    merged.machine_id = existing.machine_id;
  }
  next.set(frame.thread_id, merged);
  return next;
}
